/**
 * KitchenStaffDashboard - Main dashboard for kitchen staff after login
 * Shows menu options based on permissions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChefHat, ClipboardList, Package, BookOpen, User, LogOut,
  LayoutDashboard, Settings, Bell, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

// Sub-components
import KitchenDisplay from './KitchenDisplay';
import RecipeManager from './RecipeManager';
import StockManagement from './StockManagement';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KitchenStaffDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [staffInfo, setStaffInfo] = useState(null);
  const [stats, setStats] = useState(null);

  const token = getToken();
  // Get permissions - check dining staff first, then fall back to admin (admin has all permissions)
  const diningPermissions = JSON.parse(localStorage.getItem('dining_staff_permissions') || '[]');
  const adminRole = localStorage.getItem('admin_role');
  const isAdmin = !!adminRole || !!getToken();
  // Admins get full access
  const permissions = diningPermissions.length > 0 ? diningPermissions : (isAdmin ? ['*'] : []);
  const staffName = localStorage.getItem('dining_staff_name') || localStorage.getItem('admin_name') || 'Staff';
  const staffRole = localStorage.getItem('dining_staff_role') || adminRole || 'staff';

  // Check permissions
  const canViewOrders = permissions.includes('kds.view_orders') || permissions.includes('*');
  const canUpdateStatus = permissions.includes('kds.update_status') || permissions.includes('*');
  const canViewRecipes = permissions.includes('kds.view_recipes') || permissions.includes('kds.manage_recipes') || permissions.includes('*');
  const canManageRecipes = permissions.includes('kds.manage_recipes') || permissions.includes('*');
  const canViewStock = permissions.includes('kds.view_stock') || permissions.includes('*');

  useEffect(() => {
    // Fetch stats
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/dining/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setStats(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('eventflow_token');
    localStorage.removeItem('token');
    localStorage.removeItem('dining_staff_token');
    localStorage.removeItem('dining_staff_type');
    localStorage.removeItem('dining_staff_role');
    localStorage.removeItem('dining_staff_name');
    localStorage.removeItem('dining_staff_permissions');
    toast.success('Logged out successfully');
    navigate('/dining/login');
  };

  const roleNames = {
    'head_chef': 'Head Chef',
    'line_cook': 'Line Cook',
    'prep_cook': 'Prep Cook',
    'kitchen_helper': 'Kitchen Helper'
  };

  // Render based on active view
  if (activeView === 'kds') {
    return (
      <div className="min-h-screen">
        <div className="fixed top-4 left-4 z-50">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white shadow-lg"
            onClick={() => setActiveView('dashboard')}
          >
            ← Back to Dashboard
          </Button>
        </div>
        <KitchenDisplay />
      </div>
    );
  }

  if (activeView === 'recipes') {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="mb-4"
            onClick={() => setActiveView('dashboard')}
          >
            ← Back to Dashboard
          </Button>
          <RecipeManager />
        </div>
      </div>
    );
  }

  if (activeView === 'stock') {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="mb-4"
            onClick={() => setActiveView('dashboard')}
          >
            ← Back to Dashboard
          </Button>
          <StockManagement />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50" data-testid="kitchen-staff-dashboard">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                <ChefHat className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Kitchen Portal</h1>
                <p className="text-sm text-gray-500">Welcome back, {staffName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-orange-100 text-orange-800">
                {roleNames[staffRole] || staffRole}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => setActiveView('profile')}>
                <User className="h-4 w-4 mr-2" />
                Profile
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeView === 'profile' ? (
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                My Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{staffName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <Badge className="bg-orange-100 text-orange-800">
                  {roleNames[staffRole] || staffRole}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {permissions.map((perm, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {perm.split('.')[1] || perm}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button className="w-full mt-4" onClick={() => setActiveView('dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Quick Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Pending Orders</p>
                        <p className="text-3xl font-bold text-yellow-600">{stats.pending_orders || 0}</p>
                      </div>
                      <Bell className="h-8 w-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Preparing</p>
                        <p className="text-3xl font-bold text-orange-600">{stats.preparing_orders || 0}</p>
                      </div>
                      <Clock className="h-8 w-8 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Today's Orders</p>
                        <p className="text-3xl font-bold text-blue-600">{stats.today_orders || 0}</p>
                      </div>
                      <ClipboardList className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Low Stock</p>
                        <p className="text-3xl font-bold text-red-600">{stats.low_stock_count || 0}</p>
                      </div>
                      <Package className="h-8 w-8 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Menu Options */}
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Kitchen Display */}
              {canViewOrders && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-orange-300"
                  onClick={() => setActiveView('kds')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-2">
                      <ChefHat className="h-8 w-8 text-orange-600" />
                    </div>
                    <CardTitle>Kitchen Display</CardTitle>
                    <CardDescription>View and manage incoming orders in real-time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-orange-500 hover:bg-orange-600">
                      Open KDS
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Recipes */}
              {canViewRecipes && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-green-300"
                  onClick={() => setActiveView('recipes')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-2">
                      <BookOpen className="h-8 w-8 text-green-600" />
                    </div>
                    <CardTitle>Recipes</CardTitle>
                    <CardDescription>
                      {canManageRecipes ? 'View and manage recipes, calculate quantities' : 'View recipes and preparation guides'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      View Recipes
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Stock */}
              {canViewStock && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-blue-300"
                  onClick={() => setActiveView('stock')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-2">
                      <Package className="h-8 w-8 text-blue-600" />
                    </div>
                    <CardTitle>Inventory</CardTitle>
                    <CardDescription>Check stock levels and ingredient availability</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      Check Stock
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default KitchenStaffDashboard;
